import React, { useMemo } from "react";
import * as THREE from "three";
import { getLoadVisualScale } from "@/utils/gridScale";

type Axis = "X" | "Y" | "Z";

interface CircularMomentArrowProps {
  position: THREE.Vector3;
  axis: Axis; // Axis of rotation (moment about this axis)
  value: number; // Magnitude (sign indicates direction)
  color?: string;
  radius?: number; // Base ring radius
  tube?: number; // Ring thickness
  opacity?: number;
  // Visual config (optional)
  coneRadius?: number; // arrow head base radius
  coneLength?: number; // arrow head length
  scaleFactor?: number; // Scale range for visual sizing
}

// Renders a circular arrow (ring + small cone) around the node to represent a moment about an axis.
// Sign of value determines direction (CCW for positive, CW for negative when looking along +axis).
const planeDefaultColors: Record<Axis, string> = {
  X: "#FF0000", // dx - red
  Y: "#22C55E", // dy - green
  Z: "#2563EB", // dz - blue
};

// Exported defaults so other components (like TrussIndicator) can reuse the same visuals
export const DEFAULT_CIRCULAR_MOMENT_ARROW = {
  radius: 1.2,
  tube: 0.06,
  coneRadius: 0.18,
  coneLength: 0.35,
};

export const CircularMomentArrow: React.FC<CircularMomentArrowProps> = ({
  position,
  axis,
  value,
  color,
  radius = DEFAULT_CIRCULAR_MOMENT_ARROW.radius,
  tube = DEFAULT_CIRCULAR_MOMENT_ARROW.tube,
  opacity = 1,
  coneRadius = DEFAULT_CIRCULAR_MOMENT_ARROW.coneRadius,
  coneLength = DEFAULT_CIRCULAR_MOMENT_ARROW.coneLength,
  scaleFactor = 1,
}) => {
  const resolvedColor = color || planeDefaultColors[axis];

  // Get load visual scale from pre-calculated fixed values
  const visualScale = getLoadVisualScale(scaleFactor);

  // Apply visual scale to all dimensions
  const scaledRadius = radius * visualScale;
  const scaledTube = tube * visualScale;
  const scaledConeRadius = coneRadius * visualScale;
  const scaledConeLength = coneLength * visualScale;

  // Rotation direction: CCW for positive value, CW for negative (when looking along +axis)
  const isClockwise = value > 0;

  // Arc length reduced by 30% (from 75% to ~52.5% of full circle)
  const arcLength = Math.PI * 1.5 * 0.7; // 30% reduction

  // Add axis-specific offset to prevent collision when multiple arrows are present
  const axisOffset = useMemo(() => {
    switch (axis) {
      case "X":
        return 0;
      case "Y":
        return Math.PI * 0.67;
      case "Z":
        return Math.PI * 1.33;
      default:
        return 0;
    }
  }, [axis]);

  // Arc start angle - determines where the arc begins
  const arcStartAngle = axisOffset;

  // Head is at the END of the arc (where the arrow points)
  // For CCW: end is at arcStartAngle + arcLength
  // For CW: end is at arcStartAngle (arc goes backwards)
  const headAngle = isClockwise ? arcStartAngle : arcStartAngle + arcLength;

  // Position of arrow head on the circle
  const headPos = useMemo(() => {
    return new THREE.Vector3(
      scaledRadius * Math.cos(headAngle),
      scaledRadius * Math.sin(headAngle),
      0
    );
  }, [scaledRadius, headAngle]);

  // Tangent direction at head position (perpendicular to radius, in direction of motion)
  // For CCW: tangent is 90° ahead of radial direction
  // For CW: tangent is 90° behind (opposite)
  const tangent = useMemo(() => {
    // Tangent at angle θ is (-sin(θ), cos(θ)) for CCW motion
    const t = new THREE.Vector3(
      -Math.sin(headAngle),
      Math.cos(headAngle),
      0
    ).normalize();
    // For CW motion, reverse tangent
    if (isClockwise) t.multiplyScalar(-1);
    return t;
  }, [headAngle, isClockwise]);

  // Quaternion to rotate cone so its tip points along tangent
  // Cone geometry has tip at +Y in local space
  const headQuat = useMemo(() => {
    const coneTipDefault = new THREE.Vector3(0, 1, 0); // Cone tip points +Y
    const q = new THREE.Quaternion();
    q.setFromUnitVectors(coneTipDefault, tangent);
    return q;
  }, [tangent]);

  // Offset head slightly along tangent so cone base connects to arc end
  const headWorldPos = useMemo(() => {
    return headPos.clone().add(tangent.clone().multiplyScalar(scaledConeLength / 2));
  }, [headPos, tangent, scaledConeLength]);

  // Group rotation to orient the whole arrow for the correct axis
  // Default: arc lies in XY plane (moment about Z axis)
  const groupRotation = useMemo(() => {
    switch (axis) {
      case "X":
        return new THREE.Euler(0, Math.PI / 2, 0); // Rotate to YZ plane
      case "Y":
        return new THREE.Euler(-Math.PI / 2, 0, 0); // Rotate to XZ plane
      default:
        return new THREE.Euler(0, 0, 0); // XY plane for Z axis
    }
  }, [axis]);

  return (
    <group position={position} rotation={groupRotation}>
      {/* Arc (partial torus) */}
      <mesh rotation={[0, 0, arcStartAngle]}>
        <torusGeometry args={[scaledRadius, scaledTube, 12, 64, arcLength]} />
        <meshStandardMaterial
          color={resolvedColor}
          transparent
          opacity={opacity}
        />
      </mesh>

      {/* Arrow head (cone) - positioned at arc end, pointing along tangent */}
      <mesh position={headWorldPos} quaternion={headQuat}>
        <coneGeometry args={[scaledConeRadius, scaledConeLength, 12]} />
        <meshStandardMaterial
          color={resolvedColor}
          transparent
          opacity={opacity}
        />
      </mesh>
    </group>
  );
};

export default CircularMomentArrow;
